import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  LoadingState,
  cn,
} from '@sitewise/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  dismiss,
  fetchInbox,
  markAllRead,
  markRead,
  type InboxNotification,
} from '../../../api/notifications.js';

const CATEGORY_LABELS: Record<string, string> = {
  work_order: 'Work orders',
  violation: 'Violations',
  compliance: 'Compliance',
  inspection: 'Inspections',
  vendor: 'Vendor',
  reminder: 'Reminders',
  system: 'System',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'work_order', label: 'Work orders' },
  { id: 'violation', label: 'Violations' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'inspection', label: 'Inspections' },
  { id: 'reminder', label: 'Reminders' },
  { id: 'system', label: 'System' },
] as const;

type FilterId = (typeof FILTERS)[number]['id'];

export function NotificationsInboxPage({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterId>('all');

  const inbox = useInfiniteQuery({
    queryKey: ['notifications', 'inbox', filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      fetchInbox({
        ...(pageParam !== undefined ? { cursor: pageParam } : {}),
        limit: 30,
        ...(filter === 'unread' ? { unread: true } : {}),
        ...(filter !== 'all' && filter !== 'unread' ? { category: filter } : {}),
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const readMutation = useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const dismissMutation = useMutation({
    mutationFn: (id: string) => dismiss(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const readAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const all = inbox.data?.pages.flatMap((p) => p.notifications) ?? [];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        {embedded ? (
          <p className="text-sm text-fg-secondary">Unread items stay here until you dismiss them.</p>
        ) : (
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
            <p className="text-sm text-fg-secondary">
              Everything we've pinged you about, across email, SMS, and in-app.
            </p>
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => readAllMutation.mutate()}
          disabled={readAllMutation.isPending}
        >
          Mark all read
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f.id}
            variant={filter === f.id ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {inbox.isLoading ? (
            <div className="p-6">
              <LoadingState rows={4} />
            </div>
          ) : inbox.isError ? (
            <div className="p-6">
              <Alert variant="danger">
                <AlertDescription>Could not load notifications.</AlertDescription>
              </Alert>
            </div>
          ) : all.length === 0 ? (
            <div className="p-12 text-center text-sm text-fg-muted">
              No notifications match this filter.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {all.map((n) => (
                <InboxRow
                  key={n.id}
                  n={n}
                  onRead={() => readMutation.mutate(n.id)}
                  onDismiss={() => dismissMutation.mutate(n.id)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {inbox.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => inbox.fetchNextPage()}
            disabled={inbox.isFetchingNextPage}
          >
            {inbox.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function InboxRow({
  n,
  onRead,
  onDismiss,
}: {
  n: InboxNotification;
  onRead: () => void;
  onDismiss: () => void;
}) {
  const suppressedChannels = n.channelsAttempted.filter(
    (c) => !n.channelsDelivered.includes(c),
  );

  return (
    <li className={cn('flex items-start gap-4 px-4 py-3 hover:bg-surface-muted/40', !n.readAt && 'bg-primary-subtle/30')}>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium">{n.title}</div>
          {n.urgency === 'urgent' ? (
            <Badge variant="danger" className="px-1.5 py-0 text-[10px]">
              Urgent
            </Badge>
          ) : null}
          <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
            {CATEGORY_LABELS[n.category] ?? n.category}
          </Badge>
        </div>
        <div className="mt-1 text-sm text-fg-secondary">{n.body}</div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
          <span>{new Date(n.createdAt).toLocaleString()}</span>
          {n.channelsDelivered.length > 0 ? (
            <>
              <span aria-hidden>•</span>
              <span>Sent via {n.channelsDelivered.join(', ')}</span>
            </>
          ) : null}
          {suppressedChannels.length > 0 ? (
            <>
              <span aria-hidden>•</span>
              <span className="italic text-fg-muted">
                Suppressed: {suppressedChannels.join(', ')}
              </span>
            </>
          ) : null}
          {n.actionUrl ? (
            <Link
              to={toRelativePath(n.actionUrl)}
              className="ml-auto font-medium text-primary hover:underline"
              onClick={onRead}
            >
              Open
            </Link>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {!n.readAt ? (
          <Button variant="ghost" size="icon" aria-label="Mark read" onClick={onRead}>
            <Check className="h-3.5 w-3.5" />
          </Button>
        ) : null}
        <Button variant="ghost" size="icon" aria-label="Dismiss" onClick={onDismiss}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </li>
  );
}

function toRelativePath(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin === window.location.origin) return `${u.pathname}${u.search}${u.hash}`;
    return url;
  } catch {
    return url;
  }
}
